import { z } from 'zod';

/**
 * Roles and permissions.
 *
 * The authoritative role -> permission binding lives in the WorkOS dashboard and
 * is delivered to the API inside the access token (`role` + `permissions` claims).
 * The catalog below is the shared, typed contract used by the API to gate actions
 * and by the frontend to show/hide UI. `ROLE_PERMISSIONS` mirrors the WorkOS
 * configuration for reference/tests only — it is not the runtime source of truth.
 */

export const RoleSchema = z.enum(['admin', 'user', 'agent', 'reviewer']);
export type Role = z.infer<typeof RoleSchema>;

export const PERMISSIONS = {
  PROPERTIES_READ: 'properties:read',
  PROPERTIES_WRITE: 'properties:write',
  PROPERTIES_CREATE: 'properties:create',
  TASKS_READ: 'tasks:read',
  TASKS_ASSIGN: 'tasks:assign',
  ROLES_ASSIGN: 'roles:assign',
  REGIONS_UPDATE: 'regions:update',
  AUDIT_READ: 'audit:read',
  INSPECTIONS_READ: 'inspections:read',
  INSPECTIONS_SUBMIT: 'inspections:submit',
  INSPECTIONS_REVIEW: 'inspections:review',
  MEDIA_WRITE: 'media:write',
  FEEDBACK_READ: 'feedback:read',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const ALL_PERMISSIONS: Permission[] = Object.values(PERMISSIONS);

/**
 * Reference mapping of role -> permissions. Keep in sync with the WorkOS
 * dashboard. `reviewer` is defined ahead of the workflow being built so the
 * role can be enabled with no code changes.
 */
export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  admin: ALL_PERMISSIONS.filter((p) => p !== PERMISSIONS.PROPERTIES_CREATE),
  user: [
    PERMISSIONS.PROPERTIES_READ,
    PERMISSIONS.PROPERTIES_WRITE,
    PERMISSIONS.PROPERTIES_CREATE,
    PERMISSIONS.INSPECTIONS_READ,
  ],
  agent: [
    PERMISSIONS.TASKS_READ,
    // An agent needs this to open the report form at all: it loads the property so the
    // InspectionGate can measure the distance to its accessPin, and `GET /properties/:id` is
    // guarded by properties:read. Access stays task-scoped inside the query
    // (`findPropertyByIdForAgent` joins on a task assigned to them), so this grants an agent
    // nothing beyond the properties they are actually working.
    // Confirmed present in the WorkOS dashboard — this table had simply fallen behind it, which
    // is the failure mode of a mirror nothing checks. TODO: assert the two against each other in
    // the t4 vendor suite, where a real Staging token can be read.
    PERMISSIONS.PROPERTIES_READ,
    PERMISSIONS.INSPECTIONS_READ,
    PERMISSIONS.INSPECTIONS_SUBMIT,
    PERMISSIONS.MEDIA_WRITE,
  ],
  reviewer: [PERMISSIONS.INSPECTIONS_READ, PERMISSIONS.INSPECTIONS_REVIEW],
};

/**
 * Authentication context passed to service methods.
 *
 * `role` and `permissions` are read directly from the verified WorkOS access
 * token. `permissions` defaults to an empty array when the token predates the
 * WorkOS Roles & Permissions configuration, which fails safe (authenticated but
 * unauthorized for any gated action).
 */
export interface AuthContext {
  userId: string;
  email: string;
  role?: Role;
  permissions: Permission[];
  organizationId?: string;
}

/**
 * Local-auth token request/response (`POST /internal/local-auth/token`).
 *
 * Only served when the API runs with `AUTH_DRIVER=local` — `pnpm dev` and the T3 `stack`
 * test tier. It is the whole "log in" operation for those environments: no hosted page, no
 * network round trip, a real signed JWT out the other side. See
 * `apps/api/src/lib/auth/providers/local.provider.ts`.
 */
export const LocalAuthTokenRequestSchema = z.object({
  email: z.string().email(),
  role: RoleSchema,
  name: z.string().nullish(),
  /** Short values let a test drive the expired-session path deterministically. */
  expiresInSeconds: z.number().int().positive().max(86_400).optional(),
});
export type LocalAuthTokenRequest = z.infer<typeof LocalAuthTokenRequestSchema>;

export const LocalAuthTokenResponseSchema = z.object({
  accessToken: z.string(),
  user: z.object({
    id: z.string(),
    email: z.string(),
    name: z.string().nullable(),
    role: RoleSchema,
  }),
});
export type LocalAuthTokenResponse = z.infer<typeof LocalAuthTokenResponseSchema>;
